// MesAttestations.jsx (DESIGN AMÉLIORÉ inspiré de MesAvances)
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../MesrequetesStyles/MesAttestations.css";
import Swal from "sweetalert2";

import {
    FileText,
    Download,
    Clock,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Search,
    Filter,
    ChevronDown,
    Eye,
    Send,
    Upload,
    X,
    Loader2,
    Info,
    Plus,
    Calendar,
    TrendingUp,
} from "lucide-react";

import {
    fetchAttestationsRequest,
    getMyAttestationRequestsRequest,
    createAttestationRequestRequest,
    downloadAttestationPdfRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";

const MesAttestations = () => {
    const dispatch = useDispatch();

    const attState = useSelector((state) => state.attestations || {});
    const attestations = attState.attestations || [];
    const myRequests = attState.myRequests || [];
    const loading = !!attState.loading;
    const apiError = attState.error || "";
    const apiMessage = attState.message || "";

    const [activeTab, setActiveTab] = useState("disponibles");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [filters, setFilters] = useState({
        type: "",
        statut: "",
        dateDebut: "",
        dateFin: "",
    });

    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [selectedAttestation, setSelectedAttestation] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motif, setMotif] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [dateLimitePreparation, setDateLimitePreparation] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        dispatch(fetchAttestationsRequest());
        dispatch(getMyAttestationRequestsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (apiMessage) {
            Swal.fire({
                icon: "success",
                title: "Succès",
                text: String(apiMessage),
                timer: 1800,
                showConfirmButton: false,
            });
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1200);
            return () => clearTimeout(t);
        }
    }, [apiMessage, dispatch]);

    useEffect(() => {
        if (apiError) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: String(apiError),
                timer: 2200,
                showConfirmButton: false,
            });
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, dispatch]);

    const normalizeMode = (a) => String(a?.downloadMode || a?.mode || "").toUpperCase();
    const normalizeColor = (a, fallback = "#204385") => a?.couleur || a?.color || fallback;

    // ====== Stats ======
    const collaborateurStats = useMemo(() => {
        const demandesEnCours = myRequests.filter(
            req => req.status === "PENDING_MANAGER" || req.status === "PENDING_ADMIN"
        ).length;

        const telechargeesTotal = myRequests.filter(
            req => req.status === "APPROVED"
        ).length;

        const rejeteesTotal = myRequests.filter(
            req => req.status === "REJECTED"
        ).length;

        return {
            demandesEnCours,
            telechargeesTotal,
            rejeteesTotal,
            totalDemandes: myRequests.length,
        };
    }, [myRequests]);

    // ====== Catalogue ======
    const catalogue = useMemo(() => {
        return (attestations || [])
            .map((a) => {
                const mode = normalizeMode(a);
                return {
                    id: a.id,
                    type: a.titre || a.nom || a.name || "Attestation",
                    description: a.description || a.details || "—",
                    couleur: normalizeColor(a, mode === "FREE" ? "#10b981" : "#f59e0b"),
                    telechargerLibre: mode === "FREE",
                    actif: a.actif !== false,
                    delaiTraitement: mode === "FREE" ? "Immédiat" : "2-5 jours",
                };
            })
            .filter((x) => x.actif);
    }, [attestations]);

    // ====== Historique ======
    const historique = useMemo(() => {
        const toUpper = (v) => String(v || "").toUpperCase();
        return (myRequests || []).map((r) => {
            const status = toUpper(r.status || r.statut);
            const couleur = normalizeColor(r?.attestation, r?.couleur || "#3b82f6");

            const statutUI =
                status === "APPROVED"
                    ? "telecharge"
                    : status === "REJECTED"
                        ? "rejete"
                        : "enCours";

            return {
                id: r.id,
                type: r.attestationTitre || r.attestationNom || r.attestationName || r.attestation?.titre || "Attestation",
                couleur,
                dateDemande: r.createdAt || r.dateDemande || "",
                dateObtention: r.dateObtention || r.approvedAt || "",
                statut: statutUI,
                motif: r.motif || r.commentaire || "",
                commentaire: r.commentaire || "",
                dateLimitePreparation: r.dateLimitePreparation || "",
                statusRaw: status,
                canDownload: status === "APPROVED",
                managerDecision: r.managerDecision,
                managerName: r.managerName,
                managerComment: r.managerComment,
                managerDecisionAt: r.managerDecisionAt,
                adminDecision: r.adminDecision,
                adminName: r.adminName,
                adminComment: r.adminComment,
                adminDecisionAt: r.adminDecisionAt,
                justificatifs: r.justificatifs || [],
            };
        });
    }, [myRequests]);

    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const getStatutBadge = (statut, statusRaw) => {
        if (statut === "telecharge") {
            return (
                <span className="maBadge maBadge--ok">
                    <CheckCircle2 size={14} />
                    Téléchargée
                </span>
            );
        }

        if (statut === "rejete") {
            return (
                <span className="maBadge maBadge--no">
                    <XCircle size={14} />
                    Rejetée
                </span>
            );
        }

        const label =
            statusRaw === "PENDING_MANAGER"
                ? "En attente du manager"
                : statusRaw === "PENDING_ADMIN"
                    ? "En attente de l'admin RH"
                    : "En attente";

        return (
            <span className="maBadge maBadge--warn">
                <Clock size={14} />
                {label}
            </span>
        );
    };

    const typesUniques = useMemo(() => {
        return [...new Set(historique.map((h) => h?.type).filter(Boolean))];
    }, [historique]);

    const getFilteredHistorique = () => {
        let filtered = (historique || []).filter(Boolean);

        if (searchTerm.trim()) {
            const t = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (h) =>
                    (h.type || "").toLowerCase().includes(t) ||
                    (h.motif || "").toLowerCase().includes(t)
            );
        }

        if (filters.type) filtered = filtered.filter((h) => h.type === filters.type);
        if (filters.statut) filtered = filtered.filter((h) => h.statut === filters.statut);
        if (filters.dateDebut) filtered = filtered.filter((h) => String(h.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin) filtered = filtered.filter((h) => String(h.dateDemande).slice(0, 10) <= filters.dateFin);

        return filtered;
    };

    const openMotifModal = (att) => {
        setSelectedAttestation(att);
        setMotif("");
        setCommentaire("");
        setDateLimitePreparation("");
        setAttachments([]);
        setShowDemandeModal(true);
    };

    const resetForm = () => {
        setMotif("");
        setCommentaire("");
        setDateLimitePreparation("");
        setAttachments([]);
        setSelectedAttestation(null);
    };

    const submitMotif = (e) => {
        e.preventDefault();
        if (!selectedAttestation?.id) return;

        dispatch(
            createAttestationRequestRequest({
                attestationId: selectedAttestation.id,
                motif: motif || "",
                commentaire: commentaire || "",
                dateLimitePreparation: dateLimitePreparation || "",
                attachments: attachments || [],
            })
        );

        setShowDemandeModal(false);
        resetForm();

        // Passer à l'historique après 1 seconde
        setTimeout(() => {
            setActiveTab("historique");
        }, 1000);
    };

    const downloadFree = async (att) => {
        if (!att?.id) return;
        setDownloadingId(att.id);
        try {
            dispatch(downloadAttestationPdfRequest({ attestationId: att.id, filename: `${att.type}.pdf` }));
        } finally {
            setTimeout(() => setDownloadingId(null), 700);
        }
    };

    const downloadFromRequest = async (h) => {
        if (!h?.id) return;
        setDownloadingId(h.id);
        try {
            dispatch(downloadAttestationRequestPdfRequest({ requestId: h.id, filename: `${h.type}.pdf` }));
        } finally {
            setTimeout(() => setDownloadingId(null), 700);
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setAttachments((p) => [...p, ...files]);
    };

    const removeFile = (index) => {
        setAttachments((p) => p.filter((_, i) => i !== index));
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    if (loading && myRequests.length === 0 && attestations.length === 0) {
        return (
            <div className="maPage">
                <div className="maWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader2 size={32} className="maSpin" />
                </div>
            </div>
        );
    }

    return (
        <div className="maPage">
            <div className="maWrap">
                {/* Header */}
                <div className="maHeader">
                    <h1>Mes attestations</h1>
                    <p>Téléchargez vos attestations et suivez l'état de vos demandes</p>
                </div>

                {/* Stats */}
                {/*<div className="maStats">*/}
                {/*    <div className="maStatCard">*/}
                {/*        <div className="maStatLeft">*/}
                {/*            <div className="maStatIcon maStatIcon--accent">*/}
                {/*                <FileText size={22} />*/}
                {/*            </div>*/}
                {/*            <div>*/}
                {/*                <div className="maStatLabel">Total demandes</div>*/}
                {/*                <div className="maStatValue maStatValue--pri">*/}
                {/*                    {collaborateurStats.totalDemandes}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}

                {/*    <div className="maStatCard">*/}
                {/*        <div className="maStatLeft">*/}
                {/*            <div className="maStatIcon maStatIcon--pri">*/}
                {/*                <Clock size={22} />*/}
                {/*            </div>*/}
                {/*            <div>*/}
                {/*                <div className="maStatLabel">En cours</div>*/}
                {/*                <div className="maStatValue maStatValue--pri">*/}
                {/*                    {collaborateurStats.demandesEnCours}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}

                {/*    <div className="maStatCard">*/}
                {/*        <div className="maStatLeft">*/}
                {/*            <div className="maStatIcon maStatIcon--ok">*/}
                {/*                <CheckCircle2 size={22} />*/}
                {/*            </div>*/}
                {/*            <div>*/}
                {/*                <div className="maStatLabel">Téléchargées</div>*/}
                {/*                <div className="maStatValue maStatValue--pri">*/}
                {/*                    {collaborateurStats.telechargeesTotal}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}

                {/*    <div className="maStatCard">*/}
                {/*        <div className="maStatLeft">*/}
                {/*            <div className="maStatIcon maStatIcon--no">*/}
                {/*                <XCircle size={22} />*/}
                {/*            </div>*/}
                {/*            <div>*/}
                {/*                <div className="maStatLabel">Rejetées</div>*/}
                {/*                <div className="maStatValue maStatValue--pri">*/}
                {/*                    {collaborateurStats.rejeteesTotal}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Info sécurité */}
                <div className="maInfoCard">
                    <Info size={18} />
                    <div>
                        <div className="maInfoTitle">Sécurité des documents</div>
                        <div className="maInfoText">
                            Tous les documents sont protégés par mot de passe.{" "}
                            <b>Un email contenant le mot de passe vous sera envoyé</b> à chaque téléchargement.
                        </div>
                    </div>
                </div>

                {/* Card principale */}
                <div className="maCard maCard--noPad">
                    {/* Tabs */}
                    <div className="maTabs">
                        <button
                            type="button"
                            className={`maTab ${activeTab === "disponibles" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("disponibles")}
                        >
                            <Plus size={18} />
                            Attestations disponibles
                        </button>

                        <button
                            type="button"
                            className={`maTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <FileText size={18} />
                            Mon historique
                            <span className={`maCount ${activeTab === "historique" ? "is-active" : ""}`}>
                                {historique.length}
                            </span>
                        </button>
                    </div>

                    <div className="maCardBody">
                        {/* ===== TAB: DISPONIBLES ===== */}
                        {activeTab === "disponibles" && (
                            <>
                                <div className="maSectionHead">
                                    <div>
                                        <div className="maSectionTitle">Attestations disponibles</div>
                                        <div className="maSectionSub">
                                            Sélectionnez le type d'attestation dont vous avez besoin
                                        </div>
                                    </div>
                                </div>

                                {loading && catalogue.length === 0 ? (
                                    <div className="maEmpty">
                                        <Loader2 size={42} className="maSpin" />
                                        <p>Chargement...</p>
                                    </div>
                                ) : catalogue.length === 0 ? (
                                    <div className="maEmpty">
                                        <FileText size={42} />
                                        <p>Aucune attestation disponible</p>
                                    </div>
                                ) : (
                                    <div className="maGrid2">
                                        {catalogue.map((att) => {
                                            const isDownloading = downloadingId === att.id;
                                            return (
                                                <div
                                                    key={att.id}
                                                    className="maTypeCard is-eligible"
                                                    style={{ borderColor: att.couleur }}
                                                    onClick={() => att.telechargerLibre ? downloadFree(att) : openMotifModal(att)}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className="maTypeTop">
                                                        <div className="maTypeLeft">
                                                            <div
                                                                className="maTypeIcon"
                                                                style={{
                                                                    backgroundColor: `${att.couleur}20`,
                                                                    color: att.couleur
                                                                }}
                                                            >
                                                                <FileText size={18} />
                                                            </div>
                                                            <div>
                                                                <div className="maTypeName">{att.type}</div>
                                                                <div
                                                                    className="maPill"
                                                                    style={{
                                                                        backgroundColor: att.telechargerLibre ? "#10b98120" : "#f59e0b20",
                                                                        color: att.telechargerLibre ? "#10b981" : "#f59e0b"
                                                                    }}
                                                                >
                                                                    {att.telechargerLibre ? (
                                                                        <>
                                                                            <CheckCircle2 size={12} />
                                                                            Immédiat
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <AlertCircle size={12} />
                                                                            Validation requise
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="maTypeDesc">{att.description}</div>

                                                    <div className="maTypeGrid">
                                                        <div>
                                                            <div className="maMuted">Type</div>
                                                            <div className="maStrong">Document PDF</div>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="maBtn maBtn--full"
                                                        style={{ backgroundColor: att.couleur, color: "#fff", marginTop: "12px" }}
                                                        disabled={loading || isDownloading}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            att.telechargerLibre ? downloadFree(att) : openMotifModal(att);
                                                        }}
                                                    >
                                                        {isDownloading ? (
                                                            <>
                                                                <Loader2 size={18} className="maSpin" />
                                                                Téléchargement...
                                                            </>
                                                        ) : att.telechargerLibre ? (
                                                            <>
                                                                <Download size={18} />
                                                                Télécharger
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Send size={18} />
                                                                Demander
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/*<div className="maInfoBox">*/}
                                {/*    <Info size={18} />*/}
                                {/*    <div>*/}
                                {/*        <div className="maInfoTitle">Informations importantes</div>*/}
                                {/*        <ul className="maInfoList">*/}
                                {/*            <li>Les attestations téléchargeables immédiatement sont disponibles 24h/24</li>*/}
                                {/*            <li>Les demandes nécessitant validation sont traitées sous 2-5 jours ouvrés</li>*/}
                                {/*            <li>Chaque document téléchargé est protégé par un mot de passe unique</li>*/}
                                {/*            <li>Le mot de passe vous sera envoyé par email à chaque téléchargement</li>*/}
                                {/*        </ul>*/}
                                {/*    </div>*/}
                                {/*</div>*/}
                            </>
                        )}

                        {/* ===== TAB: HISTORIQUE ===== */}
                        {activeTab === "historique" && (
                            <>
                                <div className="maFilterCard">
                                    <div className="maControls">
                                        <div className="maSearch">
                                            <Search size={18} />
                                            <input
                                                type="text"
                                                placeholder="Rechercher dans l'historique..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            className="maFilterBtn"
                                            onClick={() => setShowFilters((p) => !p)}
                                        >
                                            <Filter size={18} /> Filtres
                                            <ChevronDown size={16} className={`maChevron ${showFilters ? "is-open" : ""}`} />
                                        </button>
                                    </div>

                                    {showFilters && (
                                        <div className="maFilters">
                                            <div className="maField">
                                                <label>Type d&apos;attestation</label>
                                                <select
                                                    value={filters.type}
                                                    onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
                                                >
                                                    <option value="">Tous</option>
                                                    {typesUniques.map((t) => (
                                                        <option key={t} value={t}>
                                                            {t}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="maField">
                                                <label>Statut</label>
                                                <select
                                                    value={filters.statut}
                                                    onChange={(e) => setFilters((p) => ({ ...p, statut: e.target.value }))}
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="telecharge">Téléchargée</option>
                                                    <option value="enCours">En attente</option>
                                                    <option value="rejete">Rejetée</option>
                                                </select>
                                            </div>

                                            <div className="maField">
                                                <label>Date (après)</label>
                                                <input
                                                    type="date"
                                                    value={filters.dateDebut}
                                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                                />
                                            </div>

                                            <div className="maField">
                                                <label>Date (avant)</label>
                                                <input
                                                    type="date"
                                                    value={filters.dateFin}
                                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {loading && historique.length === 0 ? (
                                    <div className="maEmpty">
                                        <Loader2 size={42} className="maSpin" />
                                        <p>Chargement...</p>
                                    </div>
                                ) : getFilteredHistorique().length === 0 ? (
                                    <div className="maEmpty">
                                        <FileText size={42} />
                                        <p>Aucune demande enregistrée</p>
                                        <button
                                            className="maBtn maBtn--accent"
                                            onClick={() => setActiveTab("disponibles")}
                                        >
                                            <Plus size={18} />
                                            Faire une demande
                                        </button>
                                    </div>
                                ) : (
                                    <div className="maHistory">
                                        {getFilteredHistorique().map((item) => (
                                            <button
                                                key={item.id}
                                                className="maHistoryCard"
                                                onClick={() => showDetails(item)}
                                            >
                                                <div className="maHistoryTop">
                                                    <div className="maHistoryLeft">
                                                        <div
                                                            className="maHistoryIcon"
                                                            style={{
                                                                backgroundColor: `${item.couleur}20`,
                                                                color: item.couleur
                                                            }}
                                                        >
                                                            <FileText size={18} />
                                                        </div>

                                                        <div>
                                                            <div className="maHistoryTitle">{item.type}</div>
                                                            <div className="maSub">
                                                                Demandée le {formatDate(item.dateDemande)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {getStatutBadge(item.statut, item.statusRaw)}
                                                </div>

                                                <div className="maHistoryGrid">
                                                    <div>
                                                        <div className="maMuted">Motif</div>
                                                        <div className="maStrong">{item.motif || "—"}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date limite</div>
                                                        <div className="maStrong">
                                                            {item.dateLimitePreparation ? formatDate(item.dateLimitePreparation) : "—"}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Statut</div>
                                                        <div className="maStrong">
                                                            {item.statut === "telecharge" ? "Téléchargée" :
                                                                item.statut === "rejete" ? "Rejetée" : "En attente"}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Action</div>
                                                        {item.canDownload ? (
                                                            <button
                                                                type="button"
                                                                className="maBtn maBtn--sm"
                                                                style={{ backgroundColor: item.couleur, color: "#fff" }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    downloadFromRequest(item);
                                                                }}
                                                                disabled={downloadingId === item.id}
                                                            >
                                                                {downloadingId === item.id ? (
                                                                    <Loader2 size={14} className="maSpin" />
                                                                ) : (
                                                                    <Download size={14} />
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span className="maMuted">—</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {item.statut === "rejete" && (item.adminComment || item.managerComment) && (
                                                    <div className="maReject">
                                                        <div className="maMuted">Motif du rejet</div>
                                                        <div className="maRejectText">
                                                            {item.adminComment || item.managerComment}
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ===== MODAL DEMANDE ===== */}
                {showDemandeModal && selectedAttestation && (
                    <div className="maModal">
                        <div className="maModal__panel">
                            <div className="maModal__header">
                                <div className="maModal__headLeft">
                                    <div className="maModal__icon">
                                        <Send size={18} />
                                    </div>
                                    <div>
                                        <div className="maModal__title">Nouvelle demande</div>
                                        <div className="maModal__sub">{selectedAttestation.type}</div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="maIconBtn"
                                    onClick={() => {
                                        setShowDemandeModal(false);
                                        resetForm();
                                    }}
                                    aria-label="Fermer"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="maModal__body">
                                <form onSubmit={submitMotif}>
                                    {selectedAttestation && (
                                        <div className="maInfoInline">
                                            <div className="maInlineGrid">
                                                <div>
                                                    <div className="maMuted">Type</div>
                                                    <div className="maStrong" style={{ color: selectedAttestation.couleur }}>
                                                        {selectedAttestation.type}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="maMuted">Traitement</div>
                                                    <div className="maStrong">{selectedAttestation.delaiTraitement}</div>
                                                </div>
                                                <div>
                                                    <div className="maMuted">Format</div>
                                                    <div className="maStrong">PDF protégé</div>
                                                </div>
                                                <div>
                                                    <div className="maMuted">Taille</div>
                                                    <div className="maStrong">A4</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="maField">
                                        <label>
                                            Motif de la demande <span className="maReq">*</span>
                                        </label>
                                        <select
                                            value={motif}
                                            onChange={(e) => setMotif(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Sélectionner --</option>
                                            <option value="VISA">Dossier Visa</option>
                                            <option value="BANQUE">Dossier Banque</option>
                                            <option value="ADMIN">Démarches administratives</option>
                                            <option value="AUTRE">Autre</option>
                                        </select>
                                    </div>

                                    <div className="maField">
                                        <label>Commentaire (optionnel)</label>
                                        <textarea
                                            rows={4}
                                            value={commentaire}
                                            onChange={(e) => setCommentaire(e.target.value)}
                                            placeholder="Donnez plus de détails si nécessaire..."
                                        />
                                    </div>

                                    <div className="maField">
                                        <label>Date limite de préparation (optionnel)</label>
                                        <input
                                            type="date"
                                            value={dateLimitePreparation}
                                            onChange={(e) => setDateLimitePreparation(e.target.value)}
                                            min={new Date().toISOString().slice(0, 10)}
                                        />
                                        <div className="maHelp">
                                            Indiquez la date avant laquelle vous souhaitez recevoir l'attestation
                                        </div>
                                    </div>

                                    <div className="maField">
                                        <label>Documents joints (optionnel)</label>

                                        <div className="maDrop">
                                            <Upload size={28} />
                                            <div className="maDropText">Glissez vos fichiers ici ou cliquez pour parcourir</div>
                                            <input
                                                id="maFileAttestation"
                                                type="file"
                                                multiple
                                                onChange={handleFileUpload}
                                                hidden
                                            />
                                            <label className="maBtn maBtn--outline" htmlFor="maFileAttestation">
                                                Parcourir
                                            </label>
                                        </div>

                                        {attachments.length > 0 && (
                                            <div className="maFiles">
                                                {attachments.map((file, index) => (
                                                    <div key={index} className="maFileRow">
                                                        <span>{file.name || file}</span>
                                                        <button
                                                            type="button"
                                                            className="maFileX"
                                                            onClick={() => removeFile(index)}
                                                            aria-label="Supprimer"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="maHelp" style={{ marginTop: 8 }}>
                                            Formats acceptés: PDF, JPG, PNG (max 5 Mo par fichier)
                                        </div>
                                    </div>

                                    <div className="maModal__footer">
                                        <button
                                            type="button"
                                            className="maBtn maBtn--ghost"
                                            onClick={() => {
                                                setShowDemandeModal(false);
                                                resetForm();
                                            }}
                                        >
                                            Annuler
                                        </button>

                                        <button
                                            type="submit"
                                            className="maBtn maBtn--accent"
                                            disabled={loading || !motif.trim()}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 size={16} className="maSpin" />
                                                    Envoi en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={18} />
                                                    Envoyer la demande
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* ===== MODAL DÉTAILS ===== */}
                {showDetailsModal && selectedDemande && (
                    <div className="maModal">
                        <div className="maModal__panel">
                            <div className="maModal__header">
                                <div className="maModal__headLeft">
                                    <div className="maModal__icon">
                                        <FileText size={18} />
                                    </div>
                                    <div>
                                        <div className="maModal__title">Détails de la demande #{selectedDemande.id}</div>
                                        <div className="maModal__sub">{selectedDemande.type}</div>
                                    </div>
                                </div>

                                <button
                                    className="maIconBtn"
                                    onClick={() => setShowDetailsModal(false)}
                                    aria-label="Fermer"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="maModal__body">
                                <div style={{ marginBottom: 10 }}>
                                    {getStatutBadge(selectedDemande.statut, selectedDemande.statusRaw)}
                                </div>

                                <div className="maDetailsGrid">
                                    <div className="maDetailCard">
                                        <div className="maMuted">Date de la demande</div>
                                        <div className="maStrong">{formatDate(selectedDemande.dateDemande)}</div>
                                    </div>
                                    <div className="maDetailCard">
                                        <div className="maMuted">Motif</div>
                                        <div className="maStrong">{selectedDemande.motif || "—"}</div>
                                    </div>
                                    <div className="maDetailCard">
                                        <div className="maMuted">Date limite</div>
                                        <div className="maStrong">
                                            {selectedDemande.dateLimitePreparation
                                                ? formatDate(selectedDemande.dateLimitePreparation)
                                                : "—"}
                                        </div>
                                    </div>
                                    <div className="maDetailCard">
                                        <div className="maMuted">Statut</div>
                                        <div className="maStrong">
                                            {selectedDemande.statut === "telecharge" ? "Téléchargée" :
                                                selectedDemande.statut === "rejete" ? "Rejetée" : "En attente"}
                                        </div>
                                    </div>
                                </div>

                                {selectedDemande.commentaire && (
                                    <div className="maBlock">
                                        <div className="maBlockTitle">Commentaire</div>
                                        <div className="maBlockText">{selectedDemande.commentaire}</div>
                                    </div>
                                )}

                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="maBlock">
                                        <div className="maBlockTitle">Documents joints</div>
                                        <div className="maPills">
                                            {selectedDemande.justificatifs.map((doc, idx) => (
                                                <span key={idx} className="maPill maPill--blue">
                                                    {doc.split('/').pop()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Validation manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="maBlock">
                                        <div className="maBlockTitle">Validation manager</div>
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Décision:</span>{" "}
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="maDetailsLine">
                                                <span className="maMuted">Par:</span> <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="maDetailsLine">
                                                <span className="maMuted">Date:</span>{" "}
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="maReject" style={{ marginTop: 8 }}>
                                                <div className="maMuted">Commentaire</div>
                                                <div className="maRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation admin */}
                                {selectedDemande.adminDecision !== null && (
                                    <div className="maBlock">
                                        <div className="maBlockTitle">Validation RH</div>
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Décision:</span>{" "}
                                            <b style={{ color: selectedDemande.adminDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.adminDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.adminName && (
                                            <div className="maDetailsLine">
                                                <span className="maMuted">Par:</span> <b>{selectedDemande.adminName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.adminDecisionAt && (
                                            <div className="maDetailsLine">
                                                <span className="maMuted">Date:</span>{" "}
                                                <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.adminComment && (
                                            <div className="maReject" style={{ marginTop: 8 }}>
                                                <div className="maMuted">Commentaire</div>
                                                <div className="maRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selectedDemande.canDownload && (
                                    <button
                                        type="button"
                                        className="maBtn maBtn--accent maBtn--full"
                                        onClick={() => {
                                            downloadFromRequest(selectedDemande);
                                            setShowDetailsModal(false);
                                        }}
                                        disabled={downloadingId === selectedDemande.id}
                                    >
                                        {downloadingId === selectedDemande.id ? (
                                            <>
                                                <Loader2 size={18} className="maSpin" />
                                                Téléchargement...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={18} />
                                                Télécharger l'attestation
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            <div className="maModal__footer">
                                <button className="maBtn maBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MesAttestations;